import React, { useEffect, useMemo, useRef, useState } from 'react';

import { Box } from '@opendoor/bricks-next/components/Box';
import { ButtonLoadingAnimation } from '@opendoor/bricks-next/components/Button/Button';
import type { TextInputProps } from '@opendoor/bricks-next/components/inputs/TextInput';
import { Text } from '@opendoor/bricks-next/components/Text';
import * as Icons from '@opendoor/bricks-next/icons';
import { debounce } from 'es-toolkit';

import { validateAddress as validateAddressHelper } from '../../helpers/addressValidation';
import type { AddressValidationResult } from '../../helpers/addressValidation';
import {
  ALLOWED_TYPES,
  buildAutocompleteUrl,
  buildPlacesUrl,
  generateGoogleMapsSessionToken,
} from '../../helpers/googleMaps';
import AutocompleteInput from '../AutocompleteInput';
import type { AutocompleteItem } from '../AutocompleteInput';

const DEBOUNCE_INVOKE_WAIT_MS = 100;

interface PredictionTerm {
  offset: number;
  value: string;
}

export interface AutoCompletePrediction extends AutocompleteItem {
  description: string;
  place_id?: string;
  addressToken?: string;
  types?: string[];
  source: string;
  terms?: PredictionTerm[];
}

export type SelectedAddress = {
  street1: string;
  city: string;
  state: string;
  postal_code: string;
  unit?: string;
  latitude: number;
  longitude: number;
  id?: string;
  source?: 'google' | 'casa';
};

export type { AddressValidationResult };

export interface AddressInputProps {
  id: string;
  placeholder?: string;
  disabled?: boolean;
  hideLabel?: boolean;
  label?: string;
  /**
   * Controlled value for the search input. When provided, the component operates in controlled mode.
   * Use with `onChange` to manage the input value externally.
   */
  value?: string;
  /**
   * Callback fired when the search input value changes. Required when using controlled mode (`value` prop).
   * Receives the new search term string.
   */
  onChange?: (searchTerm: string) => void;
  /**
   * Default value for uncontrolled mode. Sets the initial search term when the component mounts.
   * Ignored when `value` prop is provided.
   */
  defaultValue?: string;
  /**
   * When true, fetches address predictions when value is set externally (programmatic updates).
   * Also clears predictions when value is externally set to empty.
   * Does not fetch on initial mount. Default: false (only fetches on user input).
   */
  fetchOnExternalValue?: boolean;
  onAddressSelect: (address: SelectedAddress) => void;
  onAddressValidated?: (result: AddressValidationResult) => void;
  validateAddress?: boolean;
  onError?: (error: Error) => void;
  onPredictionsChange?: (predictions: AutoCompletePrediction[]) => void;
  /**
   * Callback fired when the search term changes. Useful for analytics or side effects.
   * For controlled mode state management, use `onChange` instead.
   */
  onSearchTermChange?: (searchTerm: string) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  customAction?: React.ReactNode;
  listBoxId?: string;
  listBoxAriaLabel: string;
  icon?: React.ReactNode;
  actionButton?: (onPress: () => void, isLoading?: boolean) => React.ReactNode;
  size?: TextInputProps['size'];
  supportText?: string;
  errorText?: string;
  invalid?: boolean;
  required?: boolean;
}

/**
 * @param actionButton overrides `icon` in the suffix position when provided
 */
const AddressInput: React.FC<AddressInputProps> = ({
  id,
  placeholder,
  disabled,
  hideLabel,
  label,
  value,
  onChange,
  defaultValue,
  fetchOnExternalValue = false,
  onAddressSelect,
  onAddressValidated,
  validateAddress = false,
  onError,
  onPredictionsChange,
  onSearchTermChange,
  onFocus,
  onBlur,
  customAction,
  listBoxId,
  listBoxAriaLabel,
  icon,
  actionButton,
  size,
  supportText,
  errorText,
  invalid,
  required,
}) => {
  const [predictions, setPredictions] = useState<AutoCompletePrediction[]>([]);
  const [usedPrediction, setUsedPrediction] = useState<AutoCompletePrediction | null>(null);
  const [internalSearchTerm, setInternalSearchTerm] = useState(defaultValue ?? '');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionToken] = useState(() => generateGoogleMapsSessionToken());
  const inputRef = useRef<HTMLInputElement>(null);
  const didMountRef = useRef(false);

  // Determine if component is controlled (value prop is provided)
  const isControlled = value !== undefined;
  const searchTerm = isControlled ? value : internalSearchTerm;

  const updateSearchTerm = (newValue: string) => {
    if (!isControlled) {
      setInternalSearchTerm(newValue);
    }
    onChange?.(newValue);
  };

  useEffect(() => {
    onPredictionsChange?.(predictions);
  }, [predictions, onPredictionsChange]);

  // For uncontrolled mode, notify on initial mount and when defaultValue changes
  // For controlled mode, the parent already knows the value
  useEffect(() => {
    if (!isControlled) {
      onSearchTermChange?.(searchTerm);
    }
  }, [isControlled, searchTerm, onSearchTermChange]);

  const clearPredictions = () => {
    setUsedPrediction(null);
    setPredictions([]);
  };

  const fetchAutocompleteOptions = useMemo(
    () =>
      debounce(async (input: string) => {
        if (!input) {
          clearPredictions();
          return;
        }

        try {
          const response = await fetch(buildAutocompleteUrl(input, sessionToken));
          const data = await response.json();

          // Clean descriptions by removing ', USA' and add required AutocompleteItem properties
          data.predictions.forEach((result: AutoCompletePrediction) => {
            result.description = result.description.replace(', USA', '');
            result.id = result.place_id || result.addressToken || result.description;
            result.ariaLabel = result.description;
          });

          // Filter predictions based on type and term count
          const filtered = data.predictions.filter((result: AutoCompletePrediction) => {
            result.source = data.source;
            if (data.source === 'google') {
              const terms = result.terms ?? [];
              const allowedTypes = (result.types ?? []).filter((type) =>
                ALLOWED_TYPES.includes(type),
              );
              return (
                (terms.length >= 5 && allowedTypes.length > 0) ||
                (terms.length === 4 && terms[0].value.match(/^\d+\S*\s\S+\s/) !== null)
              );
            }
            return true;
          });

          setPredictions(filtered);
        } catch (error) {
          clearPredictions();
          onError?.(error as Error);
        }
      }, DEBOUNCE_INVOKE_WAIT_MS),
    [sessionToken, onError],
  );

  // Handle external value changes when fetchOnExternalValue is enabled
  useEffect(() => {
    if (!fetchOnExternalValue) return;

    // Skip initial mount - only react to subsequent changes
    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }

    // Sync predictions based on external value change
    if (searchTerm === '') {
      clearPredictions();
    } else {
      fetchAutocompleteOptions(searchTerm);
    }
    // clearPredictions is stable (no external dependencies), safe to omit
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchOnExternalValue, searchTerm, fetchAutocompleteOptions]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    updateSearchTerm(newValue);
    fetchAutocompleteOptions(newValue);
    if (newValue === '') {
      clearPredictions();
    }
  };

  const handlePredictionSelect = async (prediction: AutoCompletePrediction) => {
    // Blur input to dismiss keyboard before async operations that may open modals
    inputRef.current?.blur();

    setUsedPrediction(prediction);
    updateSearchTerm(prediction.description);
    setPredictions([]);
    setIsLoading(true);

    const url =
      prediction.source === 'google'
        ? buildPlacesUrl({
            source: 'google',
            placeId: prediction.place_id,
            sessionToken,
          })
        : buildPlacesUrl({
            source: 'casa',
            addressToken: prediction.addressToken,
            sessionToken,
          });

    try {
      const response = await fetch(url);
      const address: SelectedAddress = await response.json();

      // Optionally validate the address
      if (validateAddress && onAddressValidated) {
        try {
          const validationResult = await validateAddressHelper(address);
          onAddressValidated(validationResult);
        } catch (validationError) {
          onError?.(validationError as Error);
        }
      }

      onAddressSelect(address);
    } catch (error) {
      onError?.(error as Error);
    } finally {
      setIsLoading(false);
    }
  };

  const renderItem = (prediction: AutoCompletePrediction) => {
    // Map size to typography for consistent styling
    const typographyMap = {
      xsmall: '$bodyMedium',
      small: '$bodyMedium',
      medium: '$bodyLarge',
      large: '$bodyXlarge',
    } as const;
    const typography = size ? typographyMap[size] : '$bodyXlarge';

    return (
      <Text
        typography={typography}
        tag="span"
        textAlign="left"
        $smallerThanMD={{
          typography: '$bodyMedium',
        }}
        px={10}
        py={2}
      >
        {prediction.description}
      </Text>
    );
  };

  const displayIcon =
    icon ??
    (isLoading ? (
      <Box position="relative" px="$4" display="block">
        <ButtonLoadingAnimation variant="secondary" size="xsmall" />
      </Box>
    ) : (
      <Icons.Search />
    ));

  let useSuffix: React.ReactNode = displayIcon;
  if (actionButton) {
    const onPress = () => {
      if (predictions.length && predictions[0]) {
        handlePredictionSelect(predictions[0]);
      } else if (usedPrediction && searchTerm === usedPrediction.description) {
        handlePredictionSelect(usedPrediction);
      }
    };
    const renderedButton = actionButton(onPress, isLoading);
    useSuffix = renderedButton;
  }

  return (
    <AutocompleteInput<AutoCompletePrediction>
      id={id}
      label={label}
      hideLabel={hideLabel}
      placeholder={placeholder}
      disabled={disabled}
      items={predictions}
      searchTerm={searchTerm}
      onSearchChange={handleInputChange}
      onItemSelect={handlePredictionSelect}
      renderItem={renderItem}
      suffix={useSuffix}
      customAction={customAction}
      listBoxId={listBoxId}
      onEscape={() => clearPredictions()}
      onFocus={onFocus}
      onBlur={onBlur}
      listBoxAriaLabel={listBoxAriaLabel}
      size={size}
      supportText={supportText}
      errorText={errorText}
      invalid={invalid}
      required={required}
      inputRef={inputRef}
    />
  );
};

export default AddressInput;
